import * as yup from 'yup';

import { collectWatchedFields, evaluateVisibilityRule } from './hooks/use-visibility-rule';
import type { VisibilityRule } from './lease-details.types';
import type {
  FieldConfig,
  FieldConstraints,
  LeaseDetailsFormConfig,
  SectionConfig,
} from './config';

const APPLICANTS_KEY = 'applicants';
const DEFAULT_REQUIRED_MESSAGE = 'This field is required';

// `yup.string().nullable()` widens its TValue past `yup.StringSchema`'s
// default, so we carry the nullable shape through every helper to avoid
// re-narrowing on each chain step.
type NullableStringSchema = yup.StringSchema<string | null | undefined>;

// Every `FieldConfig` kind stores a string (or null when untouched) in RHF form
// state, so the base schema is the same for all kinds. Nullable matches the
// `null` seeding done by `collectFieldDefaults` for non-readonly fields.
const baseStringSchema = (): NullableStringSchema => yup.string().nullable().notRequired();

// Money values arrive as comma-grouped strings (e.g. "1,500"); strip group
// separators before numeric comparison.
const parseMoneyString = (value: string): number => Number(value.replaceAll(',', ''));

const applyConstraints = (
  schema: NullableStringSchema,
  constraints?: FieldConstraints,
): NullableStringSchema => {
  if (!constraints) return schema;
  let next = schema;
  if (constraints.minLength != null) {
    next = next.min(constraints.minLength, `Must be at least ${constraints.minLength} characters`);
  }
  if (constraints.maxLength != null) {
    next = next.max(constraints.maxLength, `Must be at most ${constraints.maxLength} characters`);
  }
  if (constraints.pattern) {
    next = next.matches(constraints.pattern, {
      message: 'Invalid format',
      excludeEmptyString: true,
    });
  }
  if (constraints.min != null) {
    const min = constraints.min;
    next = next.test('min-value', `Must be at least ${min}`, (value) => {
      if (value == null || value === '') return true;
      const parsed = parseMoneyString(value);
      return Number.isFinite(parsed) && parsed >= min;
    });
  }
  if (constraints.max != null) {
    const max = constraints.max;
    next = next.test('max-value', `Must be at most ${max}`, (value) => {
      if (value == null || value === '') return true;
      const parsed = parseMoneyString(value);
      return Number.isFinite(parsed) && parsed <= max;
    });
  }
  return next;
};

// `false && x` short-circuits to `false`; `true && x` reduces to `x`; otherwise
// both sides are rules, AND'd into a fresh `all` combinator.
const andVisibility = (
  a: boolean | VisibilityRule,
  b: boolean | VisibilityRule,
): boolean | VisibilityRule => {
  if (a === false || b === false) return false;
  if (a === true) return b;
  if (b === true) return a;
  return { all: [a, b] };
};

// Applies `apply` to `schema` exactly when `rule` would show the field/section,
// mirroring how `useVisibilityRule` reacts to live form state:
//   - `true`  → apply unconditionally
//   - `false` → never apply (constraint dropped)
//   - rule with watched fields    → gate via Yup's reactive `.when(...)` over
//     the rule's minimal watched-field set
//   - rule with no watched fields → a constant combinator (e.g. `{ all: [] }`
//     is true, `{ any: [] }` is false, `{ not: { any: [] } }` is true), so
//     there is nothing for `.when()` to subscribe to — resolve it statically
//     against an empty value map.
// Yup v1's `.when([keys], cb)` invokes `cb` with the watched values as an
// array; we rebuild the map the rule evaluator expects.
const whenRulePasses = <S extends yup.AnySchema>(
  schema: S,
  rule: boolean | VisibilityRule,
  apply: (s: S) => yup.AnySchema,
): S => {
  if (rule === true) return apply(schema) as S;
  if (rule === false) return schema;
  const watched = collectWatchedFields(rule);
  if (watched.length === 0) {
    return (evaluateVisibilityRule(rule, {}) ? apply(schema) : schema) as S;
  }
  return schema.when(watched, ([...values]: unknown[], dependent: S) => {
    const valueMap: Record<string, unknown> = {};
    watched.forEach((path, index) => {
      valueMap[path] = values[index];
    });
    // No `notRequired()` on the otherwise branch — the dependent schema
    // already carries its base (e.g. `nullable().notRequired()`) shape.
    return evaluateVisibilityRule(rule, valueMap) ? apply(dependent) : dependent;
  });
};

// `required: true` → `.required()`; `required: <rule>` → required only when the
// rule passes. Visibility is handled by the outer gate in `buildFieldSchema`, so
// this is required-ness alone.
const applyRequired = <S extends yup.AnySchema>(schema: S, field: FieldConfig): S => {
  const required = field.required;
  const message = field.requiredMessage ?? DEFAULT_REQUIRED_MESSAGE;
  if (required === true) {
    return schema.required(message) as S;
  }
  if (required && typeof required === 'object') {
    return whenRulePasses(schema, required, (s) => s.required(message));
  }
  return schema;
};

// Boolean (switch/checkbox) and object (address) kinds store non-string values,
// so they branch onto a matching nullable base; every other kind stores a string.
const baseForKind = (field: FieldConfig): yup.AnySchema => {
  if (field.kind === 'switch' || field.kind === 'checkbox') {
    return yup.boolean().nullable().notRequired();
  }
  if (field.kind === 'address') {
    return yup.mixed().nullable().notRequired();
  }
  return baseStringSchema();
};

const isStringKind = (field: FieldConfig): boolean =>
  field.kind !== 'switch' && field.kind !== 'checkbox' && field.kind !== 'address';

const buildFieldSchema = (
  field: FieldConfig,
  sectionVisible: boolean | VisibilityRule,
): yup.AnySchema => {
  const effectiveVisible = andVisibility(sectionVisible, field.visible);

  // A field's *entire* validation — constraints, custom `validate`, and required —
  // applies ONLY while the field is visible. A hidden field (statically or via a
  // rule) accepts any value, so a conditionally-shown field that's hidden again
  // can't keep the form invalid with a stale out-of-range value. This matches the
  // legacy `shouldUnregister: true` behavior without unregistering fields — so
  // effect-managed hidden fields (e.g. PMI rent/deposit coverage, the auto-set
  // SMB screening result) stay registered for submit. The field stays in the
  // shape (its base) when hidden, so cross-field `.when()` chains still resolve.
  const applyValidation = (schema: yup.AnySchema): yup.AnySchema => {
    let next = schema;
    if (isStringKind(field)) {
      next = applyConstraints(next as NullableStringSchema, field.constraints);
    }
    next = applyRequired(next, field);
    if (field.validate) {
      next = next.concat(field.validate);
    }
    return next;
  };

  if (effectiveVisible === false) return baseForKind(field);
  if (effectiveVisible === true) return applyValidation(baseForKind(field));
  return whenRulePasses(baseForKind(field), effectiveVisible, applyValidation);
};

const buildFlatSectionShape = (section: SectionConfig): Record<string, yup.AnySchema> => {
  const shape: Record<string, yup.AnySchema> = {};
  for (const [name, field] of Object.entries(section.fields)) {
    shape[name] = buildFieldSchema(field, section.visible);
  }
  return shape;
};

/**
 * Derives the Yup validation schema for `<LeaseDetailsForm>` from its config.
 *
 * The engine wires this into `useForm({ resolver: yupResolver(schema) })`, so
 * filling the body alone (no engine changes) turns on validation for every
 * consumer.
 *
 * Behavior:
 *  - Walks all four sections; flat sections (leaseInformation, screening,
 *    coverage) collapse onto the top-level shape — field names must already be
 *    unique across them (the engine enforces this at runtime).
 *  - `applicants` becomes `yup.array().of(<entry shape>).min(min).max(max)`,
 *    using the section's `ApplicantsPresentation` for bounds. Those bounds are
 *    gated on the section's `visible` — a hidden section leaves the array
 *    optional and unbounded, matching how flat fields drop required-ness.
 *  - Hidden-via-rule fields are still registered (as optional) so cross-field
 *    `.when()` chains can resolve dependencies on them.
 *  - `field.required: VisibilityRule` is translated into `.when(...)` against
 *    the rule's minimal watched-field set, mirroring `useVisibilityRule`.
 *  - `field.constraints` map onto Yup primitives (min/max length, pattern,
 *    numeric min/max parsed from the string form-state).
 *  - `field.validate` is `.concat()`'d on top, so consumer schemas extend the
 *    engine's defaults rather than replace them.
 */
export const buildLeaseDetailsSchema = (config: LeaseDetailsFormConfig): yup.AnyObjectSchema => {
  const shape: Record<string, yup.AnySchema> = {
    ...buildFlatSectionShape(config.sections.leaseInformation),
    ...buildFlatSectionShape(config.sections.screening),
    ...buildFlatSectionShape(config.sections.coverage),
  };

  const applicantsConfig = config.sections.applicants;
  const entryShape: Record<string, yup.AnySchema> = {};
  for (const [name, field] of Object.entries(applicantsConfig.fields)) {
    entryShape[name] = buildFieldSchema(field, applicantsConfig.visible);
  }

  // The array bounds (min/max/required) bind only when the section is visible,
  // mirroring how flat-section fields drop their required-ness when hidden. A
  // hidden applicants section leaves the array optional and unbounded.
  const applicantsArray = yup.array().of(yup.object().shape(entryShape));
  let applicantsSchema: yup.AnySchema = whenRulePasses(
    applicantsArray,
    applicantsConfig.visible,
    (s) =>
      s
        .min(applicantsConfig.min, `At least ${applicantsConfig.min} required`)
        .max(applicantsConfig.max, `At most ${applicantsConfig.max} allowed`)
        .required(),
  );
  // Array-level rules (e.g. case-insensitive uniqueness) ride on `arrayValidate`,
  // concatenated on top of the bounds.
  if (applicantsConfig.arrayValidate) {
    applicantsSchema = applicantsSchema.concat(applicantsConfig.arrayValidate);
  }
  shape[APPLICANTS_KEY] = applicantsSchema;

  return yup.object().shape(shape);
};
